import {
  ResponseGetFixturesGuessing,
  RoutePostFixtureGuessing,
} from "@CommonApi/fixtures-guessing";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useApi } from "src/api";
import { Page, PageStatus } from "src/components";
import { useLoaders } from "src/components/InlineLoader";
import { MerchflowHeader } from "src/components/merchflows/MerchflowHeader/MerchflowHeader";
import {
  Button,
  Checkbox,
  Color,
  Flex,
  Icon,
  Link,
  Scroller,
  Search,
  SubgridTable,
  Text,
} from "src/elements";
import { useAsyncState } from "src/hooks/useAsyncState";
import { routeToUrl } from "@CommonUtils/routes";
import { dateIsoStringToLocaleString, pages } from "../../utils";
import { ImagineShelvesLogsButton } from "./components/ImagineShelvesLogsButton";
import { useModalImagineShelvesSettings } from "src/modals/ImagineShelvesSettings/store/actions";

export const PageImagineShelves = () => {
  const { categoryId: _categoryId } = useParams<{
    categoryId: string;
  }>();
  const categoryId = Number(_categoryId);
  const { getFixturesGuessingApi } = useApi();
  const { hasLoader } = useLoaders();
  const { openModalImagineShelvesSettings, imagineShelvesSettingsData } =
    useModalImagineShelvesSettings();

  const refStoresLoader = useRef<NodeJS.Timeout | null>(null);
  const [allStores, setAllStores] = useAsyncState<ResponseGetFixturesGuessing>();
  const [pendingStoreIds, setPendingStoreIds] = useState<number[]>([]);
  const [selectedStoreIds, setSelectedStoreIds] = useState<number[]>([]);
  const [search, setSearch] = useState("");

  const isGetLoading = allStores === undefined; // Only show loading on initial load, not during polling to prevent blinking
  const isPostLoading = hasLoader(`POST_${RoutePostFixtureGuessing}`);

  const loadAllStores = async () => {
    const response = await getFixturesGuessingApi({ categoryId: String(categoryId) });

    if (response) {
      setAllStores(response);
      setPendingStoreIds((pendingStoreIds) =>
        pendingStoreIds.filter((pendingStoreId) => {
          if (
            response.find(
              (store) => store.storeId === pendingStoreId && store.status === "IN_PROGRESS",
            )
          ) {
            return false;
          } else {
            return true;
          }
        }),
      );

      if (response.find((store) => store.status === "IN_PROGRESS")) {
        refStoresLoader.current = setTimeout(loadAllStores, 20_000);
      }
    }
  };

  useEffect(() => {
    loadAllStores();

    return () => {
      if (refStoresLoader.current) {
        clearTimeout(refStoresLoader.current);
      }
    };
  }, [categoryId]);

  useEffect(() => {
    if (imagineShelvesSettingsData?.started) {
      setPendingStoreIds(imagineShelvesSettingsData.storeIds);
      setSelectedStoreIds([]);
    }
  }, [imagineShelvesSettingsData]);

  // Filter stores based on search input
  const filteredStores = useMemo(() => {
    if (!allStores) return [];
    if (!search) return allStores;

    const searchLowercase = search.toLowerCase();
    return allStores.filter((store) => store.storeCode.toLowerCase().includes(searchLowercase));
  }, [allStores, search]);

  return (
    <Page>
      <MerchflowHeader imagine="SHELVES" categoryId={categoryId} />

      <Flex column flexGrow gap="10px">
        <Flex justify="between">
          <Search width="200px" search={search} setSearch={setSearch} placeholder="Search Stores" />

          <Flex gap="10px">
            <Button
              isDisabled={!allStores || selectedStoreIds.length === 0}
              isLoading={isPostLoading}
              onClick={() =>
                openModalImagineShelvesSettings({ categoryId, storeIds: selectedStoreIds })
              }
            >
              Imagine With AI
            </Button>
          </Flex>
        </Flex>

        {isGetLoading ? (
          <PageStatus label="Loading Stores..." icon="spinner" />
        ) : allStores === null ? (
          <PageStatus label="Failed to load stores..." icon="alert" color={Color.red} />
        ) : (
          <Scroller gutter="auto">
            <SubgridTable
              align="center"
              columns={[
                {
                  id: "checked",
                  header: "checked",
                  justify: "center",
                  width: "50px",
                  headerRenderer: (
                    <Checkbox
                      width="min-content"
                      isChecked={
                        selectedStoreIds.length === 0
                          ? false
                          : selectedStoreIds.length ===
                              filteredStores.filter(
                                (store) =>
                                  store.status !== "IN_PROGRESS" &&
                                  !pendingStoreIds.includes(store.storeId),
                              ).length
                            ? true
                            : "mixed"
                      }
                      setIsChecked={(checked) =>
                        setSelectedStoreIds(
                          checked
                            ? filteredStores
                                .filter(
                                  (store) =>
                                    store.status !== "IN_PROGRESS" &&
                                    !pendingStoreIds.includes(store.storeId),
                                )
                                .map((store) => store.storeId)
                            : [],
                        )
                      }
                    />
                  ),
                  renderer: ({ row }) => {
                    const isDisabled =
                      row.status === "IN_PROGRESS" || pendingStoreIds.includes(row.storeId);

                    return (
                      <Checkbox
                        width="min-content"
                        isChecked={selectedStoreIds.includes(row.storeId)}
                        isDisabled={isDisabled}
                        setIsChecked={(isChecked) =>
                          setSelectedStoreIds(
                            isChecked
                              ? [...selectedStoreIds, row.storeId]
                              : selectedStoreIds.filter(
                                  (selectedStoreId) => selectedStoreId !== row.storeId,
                                ),
                          )
                        }
                      />
                    );
                  },
                },
                {
                  id: "storeCode",
                  header: "Store Name",
                  width: "120px",
                  justify: "right",
                  renderer: ({ row: { status, fixtureGuessingId, storeCode } }) => {
                    const isEnabled = status === "DONE" && fixtureGuessingId !== null;

                    return (
                      <Link
                        to={
                          isEnabled
                            ? `${routeToUrl(
                                pages.imagineShelvesFixturesGuessing_fixturesGuessingId_,
                                {
                                  fixturesGuessingId: fixtureGuessingId,
                                },
                              )}`
                            : undefined
                        }
                        disabled={!isEnabled}
                      >
                        <Text variant="small2" color={!isEnabled && Color.textDisabled}>
                          {storeCode}
                        </Text>
                      </Link>
                    );
                  },
                },
                {
                  id: "baySize",
                  header: "Size",
                  width: "50px",
                  justify: "right",
                },
                {
                  id: "updatedDate",
                  header: "Date Updated",
                  width: "200px",
                  justify: "right",
                  renderer: ({ value }) => {
                    return value ? dateIsoStringToLocaleString(value) : null;
                  },
                },
                {
                  id: "status",
                  header: "Status",
                  width: "50px",
                  justify: "center",
                  renderer: ({ row }) => {
                    if (pendingStoreIds.includes(row.storeId) || row.status === "IN_PROGRESS") {
                      return <Icon name="spinner" size={14} />;
                    }

                    switch (row.status) {
                      case "NOT_STARTED":
                        return <Icon name="minus" size={14} color={Color.spaceGray} />;
                      case "DONE":
                        return <Icon name="checkmarkCircle" size={14} />;
                      case "ERROR":
                        return <Icon name="warningCircle" size={14} color={Color.red} />;
                    }
                  },
                },
                {
                  id: "notes",
                  header: "Notes",
                },
                {
                  id: "logs",
                  header: "Logs",
                  width: "50px",
                  justify: "center",
                  renderer: ({ row }) => (
                    <ImagineShelvesLogsButton
                      fixturesGuessingId={row.fixtureGuessingId || 0}
                      isDisabled={row.status === "NOT_STARTED"}
                    />
                  ),
                },
              ]}
              data={filteredStores}
            />
          </Scroller>
        )}
      </Flex>
    </Page>
  );
};
